<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { googleCalendarAPI } from '../../../api/googleCalendar';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Modo Telão: a tela cheia precisa ser pedida dentro do clique (o navegador recusa
// fora de um gesto do usuário), por isso é pedida aqui, antes de trocar de rota.
function abrirTelao() {
  document.documentElement.requestFullscreen?.().catch(() => {});
  router.push({
    name: 'google_calendar_telao',
    params: { accountId: route.params.accountId },
  });
}

// ── State ──────────────────────────────────────────────────────────────────
const calendars = ref([]);
const selectedCalendars = ref([]);
const events = ref([]);
const view = ref('week');
const currentDate = ref(new Date());
const loading = ref(false);
const error = ref('');
const sidebarCollapsed = ref(false);
const calendarSearch = ref('');
const eventSearch = ref('');
const showModal = ref(false);
const selectedEvent = ref(null);
// Modo do modal: 'view' | 'edit' | 'create'
const modalMode = ref('view');
const editForm = ref({
  summary: '',
  description: '',
  location: '',
  start: '',
  end: '',
  colorId: '',
  calendarId: '',
});

const views = [
  { key: 'day', label: 'Dia' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
];
const hours = Array.from({ length: 24 }, (_, i) => i);

// Paleta oficial de cores de evento do Google Calendar (colorId 1–11)
const GOOGLE_COLORS = [
  { id: '1', hex: '#7986CB', key: 'LAVENDER' },
  { id: '2', hex: '#33B679', key: 'SAGE' },
  { id: '3', hex: '#8E24AA', key: 'GRAPE' },
  { id: '4', hex: '#E67C73', key: 'FLAMINGO' },
  { id: '5', hex: '#F6BF26', key: 'BANANA' },
  { id: '6', hex: '#F4511E', key: 'TANGERINE' },
  { id: '7', hex: '#039BE5', key: 'PEACOCK' },
  { id: '8', hex: '#616161', key: 'GRAPHITE' },
  { id: '9', hex: '#3F51B5', key: 'BLUEBERRY' },
  { id: '10', hex: '#0B8043', key: 'BASIL' },
  { id: '11', hex: '#D50000', key: 'TOMATO' },
];
// Duração padrão (min) ao criar evento por clique em espaço vazio
const DEFAULT_EVENT_MINUTES = 30;

// ── Computed ───────────────────────────────────────────────────────────────
const filteredCalendars = computed(() =>
  calendars.value.filter(
    c =>
      !calendarSearch.value ||
      c.summary?.toLowerCase().includes(calendarSearch.value.toLowerCase())
  )
);

// Calendários em que o usuário tem permissão de escrita (owner/writer)
const editableCalendars = computed(() =>
  calendars.value.filter(c =>
    ['owner', 'writer'].includes((c.accessRole || '').toLowerCase())
  )
);

const dateHeader = computed(() => {
  const d = currentDate.value;
  if (view.value === 'day') {
    return d.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  if (view.value === 'week') {
    const start = new Date(d);
    start.setDate(d.getDate() - d.getDay());
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    if (start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
    }
    return `${start.toLocaleDateString('pt-BR', { month: 'short' })} – ${end.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}`;
  }
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
});

const weekDays = computed(() => {
  const start = new Date(currentDate.value);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
});

const monthDays = computed(() => {
  const d = currentDate.value;
  const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - startDate.getDay());
  const days = [];
  const cur = new Date(startDate);
  while (days.length < 42) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
});

// ── Methods ────────────────────────────────────────────────────────────────
async function loadCalendars() {
  loading.value = true;
  error.value = '';
  try {
    const res = await googleCalendarAPI.getCalendars();
    calendars.value = res.data;
    if (res.data.length > 0) {
      selectedCalendars.value = [res.data[0].id];
    }
  } catch {
    error.value =
      'Erro ao carregar calendários. Verifique a configuração OAuth em Configurações → Google Calendar.';
  } finally {
    loading.value = false;
  }
}

function getDateRange() {
  const d = currentDate.value;
  const start = new Date(d);
  const end = new Date(d);

  if (view.value === 'day') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (view.value === 'week') {
    start.setDate(d.getDate() - d.getDay());
    start.setHours(0, 0, 0, 0);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
  } else {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setMonth(d.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
  }
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

async function loadEvents() {
  if (selectedCalendars.value.length === 0) return;
  loading.value = true;
  try {
    const { startDate, endDate } = getDateRange();
    const promises = selectedCalendars.value.map(async calendarId => {
      const calendar = calendars.value.find(c => c.id === calendarId);
      try {
        const res = await googleCalendarAPI.getEvents(
          calendarId,
          startDate,
          endDate
        );
        return (Array.isArray(res.data) ? res.data : []).map(ev => ({
          ...ev,
          calendarId,
          calendarName: calendar?.summary || calendarId,
          calendarColor: calendar?.backgroundColor || '#667eea',
        }));
      } catch {
        return [];
      }
    });
    events.value = (await Promise.all(promises)).flat();
    error.value = '';
  } catch {
    error.value = 'Erro ao carregar eventos.';
  } finally {
    loading.value = false;
  }
}

function matchesSearch(ev) {
  if (!eventSearch.value.trim()) return true;
  const q = eventSearch.value.toLowerCase();
  return [
    ev.summary,
    ev.description,
    ev.location,
    ev.calendarName,
    ...(ev.attendees || []),
  ].some(v => v?.toLowerCase().includes(q));
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toDatetimeLocal(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Watchers ───────────────────────────────────────────────────────────────
watch([selectedCalendars, currentDate, view], () => {
  if (selectedCalendars.value.length > 0) loadEvents();
  else events.value = [];
});

// ── Lifecycle ──────────────────────────────────────────────────────────────
onMounted(() => loadCalendars());

// ── Methods (continued) ────────────────────────────────────────────────────
function navigate(dir) {
  const d = new Date(currentDate.value);
  if (view.value === 'day') d.setDate(d.getDate() + dir);
  else if (view.value === 'week') d.setDate(d.getDate() + dir * 7);
  else d.setMonth(d.getMonth() + dir);
  currentDate.value = d;
}

function goToToday() {
  currentDate.value = new Date();
}

function jumpToDay(day) {
  currentDate.value = new Date(day);
  view.value = 'day';
}

function toggleCalendar(id) {
  if (selectedCalendars.value.includes(id)) {
    selectedCalendars.value = selectedCalendars.value.filter(c => c !== id);
  } else {
    selectedCalendars.value = [...selectedCalendars.value, id];
  }
}

function getEventsForDay(day) {
  return events.value.filter(ev => {
    const evDate = new Date(ev.start);
    return evDate.toDateString() === day.toDateString() && matchesSearch(ev);
  });
}

function eventPosition(ev) {
  const start = new Date(ev.start);
  const top = ((start.getHours() * 60 + start.getMinutes()) / 60) * 48;
  let height = 48;
  if (ev.end) {
    const end = new Date(ev.end);
    height = Math.max(((end - start) / 3600000) * 48, 24);
  }
  return { top: `${top}px`, height: `${height}px`, position: 'absolute' };
}

function eventTooltip(ev) {
  const time = formatTime(ev.start);
  const endTime = ev.end ? ` – ${formatTime(ev.end)}` : '';
  return `${ev.summary || t('GOOGLE_CALENDAR.NO_TITLE')}\n${time}${endTime}\n${ev.calendarName || ''}`;
}

// Cor de exibição: usa a cor própria do evento (colorId) e, na ausência,
// cai para a cor do calendário.
function eventColor(ev) {
  if (ev.colorId) {
    const c = GOOGLE_COLORS.find(col => col.id === String(ev.colorId));
    if (c) return c.hex;
  }
  return ev.calendarColor || '#667eea';
}

function openModal(ev) {
  selectedEvent.value = ev;
  showModal.value = true;
  modalMode.value = 'view';
}

function closeModal() {
  showModal.value = false;
  selectedEvent.value = null;
  modalMode.value = 'view';
}

function enterEdit() {
  const ev = selectedEvent.value;
  editForm.value = {
    summary: ev.summary || '',
    description: ev.description || '',
    location: ev.location || '',
    start: toDatetimeLocal(ev.start),
    end: toDatetimeLocal(ev.end),
    colorId: ev.colorId ? String(ev.colorId) : '',
    calendarId: ev.calendarId || '',
  };
  modalMode.value = 'edit';
}

function cancelEdit() {
  // No modo criar, fechar; no modo editar, voltar para visualização
  if (modalMode.value === 'create') {
    closeModal();
  } else {
    modalMode.value = 'view';
  }
}

// Calendário de destino padrão ao criar (prioriza editável + selecionado)
function defaultTargetCalendarId() {
  const editable = editableCalendars.value;
  if (editable.length === 0) return '';
  const selectedEditable = editable.find(c =>
    selectedCalendars.value.includes(c.id)
  );
  if (selectedEditable) return selectedEditable.id;
  const primary = editable.find(c => c.primary);
  return primary ? primary.id : editable[0].id;
}

// Abre o modal em modo criação com início/fim pré-preenchidos
function openCreateModal(startDate, endDate) {
  selectedEvent.value = null;
  editForm.value = {
    summary: '',
    description: '',
    location: '',
    start: toDatetimeLocal(startDate.toISOString()),
    end: toDatetimeLocal(endDate.toISOString()),
    colorId: '',
    calendarId: defaultTargetCalendarId(),
  };
  modalMode.value = 'create';
  showModal.value = true;
}

// Clique em slot vazio nas views de semana/dia.
// Calcula a hora pelo índice e os minutos (0 ou 30) pela posição do clique.
function onSlotClick(day, hour, evt) {
  const cellHeight = evt?.currentTarget?.offsetHeight || 48;
  const minutes = (evt?.offsetY ?? 0) >= cellHeight / 2 ? 30 : 0;
  const start = new Date(day);
  start.setHours(hour, minutes, 0, 0);
  const end = new Date(start.getTime() + DEFAULT_EVENT_MINUTES * 60000);
  openCreateModal(start, end);
}

// Botão "Novo evento" na toolbar: próximo horário cheio/meia-hora
function openCreateModalNow() {
  const start = new Date(currentDate.value);
  const now = new Date();
  // Se a data atual for hoje, parte do próximo bloco de 30min; senão, 09:00
  if (start.toDateString() === now.toDateString()) {
    start.setHours(now.getHours(), now.getMinutes() < 30 ? 30 : 0, 0, 0);
    if (now.getMinutes() >= 30) start.setHours(now.getHours() + 1);
  } else {
    start.setHours(9, 0, 0, 0);
  }
  const end = new Date(start.getTime() + DEFAULT_EVENT_MINUTES * 60000);
  openCreateModal(start, end);
}

// Clique em dia vazio na view de mês: horário default 09:00
function onMonthDayClick(day) {
  const start = new Date(day);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start.getTime() + DEFAULT_EVENT_MINUTES * 60000);
  openCreateModal(start, end);
}

// O backend espera ISO-8601 sem milissegundos (yyyy-MM-dd'T'HH:mm:ssXXX).
// toISOString() inclui ".SSS", então removemos antes de enviar.
function toIsoNoMillis(value) {
  return new Date(value).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

async function saveNewEvent() {
  if (!editForm.value.summary.trim()) {
    error.value = t('GOOGLE_CALENDAR.MODAL.TITLE_REQUIRED');
    return;
  }
  if (!editForm.value.calendarId) {
    error.value = t('GOOGLE_CALENDAR.MODAL.CALENDAR_REQUIRED');
    return;
  }
  if (!editForm.value.start || !editForm.value.end) {
    error.value = t('GOOGLE_CALENDAR.MODAL.DATES_REQUIRED');
    return;
  }
  if (new Date(editForm.value.end) <= new Date(editForm.value.start)) {
    error.value = t('GOOGLE_CALENDAR.MODAL.END_AFTER_START');
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    await googleCalendarAPI.createEvent(editForm.value.calendarId, {
      summary: editForm.value.summary,
      description: editForm.value.description || null,
      location: editForm.value.location || null,
      start: toIsoNoMillis(editForm.value.start),
      end: toIsoNoMillis(editForm.value.end),
      colorId: editForm.value.colorId || null,
    });
    await loadEvents();
    closeModal();
  } catch {
    error.value = t('GOOGLE_CALENDAR.MODAL.CREATE_ERROR');
  } finally {
    loading.value = false;
  }
}

async function saveEvent() {
  if (!editForm.value.summary.trim()) {
    error.value = 'O título do evento é obrigatório.';
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    await googleCalendarAPI.updateEvent(
      selectedEvent.value.calendarId,
      selectedEvent.value.id,
      {
        summary: editForm.value.summary,
        description: editForm.value.description || null,
        location: editForm.value.location || null,
        start: editForm.value.start
          ? toIsoNoMillis(editForm.value.start)
          : selectedEvent.value.start,
        end: editForm.value.end
          ? toIsoNoMillis(editForm.value.end)
          : selectedEvent.value.end,
        colorId: editForm.value.colorId || null,
      }
    );
    await loadEvents();
    closeModal();
  } catch {
    error.value = 'Erro ao atualizar evento.';
  } finally {
    loading.value = false;
  }
}

async function confirmDelete() {
  loading.value = true;
  error.value = '';
  try {
    await googleCalendarAPI.deleteEvent(
      selectedEvent.value.calendarId,
      selectedEvent.value.id
    );
    await loadEvents();
    closeModal();
  } catch {
    error.value = 'Erro ao excluir evento.';
  } finally {
    loading.value = false;
  }
}

// ── Helpers de formatação ──────────────────────────────────────────────────
function isToday(day) {
  return day.toDateString() === new Date().toDateString();
}

function isSameMonth(day) {
  return day.getMonth() === currentDate.value.getMonth();
}

function shortWeekDay(day) {
  return day
    .toLocaleDateString('pt-BR', { weekday: 'short' })
    .replace('.', '')
    .toUpperCase();
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<template>
  <div class="flex flex-1 h-full bg-white overflow-hidden">
    <!-- Sidebar: calendários -->
    <aside
      class="flex flex-col border-r border-slate-100 bg-slate-50 transition-all duration-200 flex-shrink-0"
      :class="sidebarCollapsed ? 'w-12' : 'w-64'"
    >
      <div
        class="flex items-center justify-between px-3 py-3 border-b border-slate-100"
      >
        <span
          v-if="!sidebarCollapsed"
          class="text-sm font-semibold text-slate-700"
          >{{ t('GOOGLE_CALENDAR.CALENDARS') }}</span
        >
        <button
          class="p-1 rounded hover:bg-slate-200 text-slate-500"
          @click="sidebarCollapsed = !sidebarCollapsed"
        >
          <span
            :class="
              sidebarCollapsed
                ? 'i-lucide-chevron-right'
                : 'i-lucide-chevron-left'
            "
            class="size-4"
          />
        </button>
      </div>

      <div v-if="!sidebarCollapsed" class="flex-1 overflow-y-auto p-2">
        <!-- Busca de calendários -->
        <input
          v-model="calendarSearch"
          type="text"
          :placeholder="t('GOOGLE_CALENDAR.SEARCH_CALENDAR_PLACEHOLDER')"
          class="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-woot-500 mb-2"
        />

        <!-- Lista de calendários -->
        <div
          v-if="loading && calendars.length === 0"
          class="text-xs text-slate-400 text-center py-4"
        >
          {{ t('GOOGLE_CALENDAR.LOADING_CALENDARS') }}
        </div>
        <div
          v-else-if="filteredCalendars.length === 0 && !loading"
          class="text-xs text-slate-400 text-center py-4"
        >
          {{ t('GOOGLE_CALENDAR.NO_CALENDARS') }}
        </div>
        <label
          v-for="calendar in filteredCalendars"
          :key="calendar.id"
          class="flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer hover:bg-slate-100"
        >
          <input
            type="checkbox"
            :checked="selectedCalendars.includes(calendar.id)"
            class="hidden"
            @change="toggleCalendar(calendar.id)"
          />
          <span
            class="inline-block size-3 rounded-sm flex-shrink-0"
            :style="{ backgroundColor: calendar.backgroundColor || '#667eea' }"
          />
          <span class="text-xs text-slate-700 truncate">{{
            calendar.summary
          }}</span>
          <span
            v-if="!selectedCalendars.includes(calendar.id)"
            class="ml-auto size-3 rounded-sm border border-slate-300 flex-shrink-0"
          />
          <span
            v-else
            class="ml-auto i-lucide-check size-3 text-slate-600 flex-shrink-0"
          />
        </label>
      </div>
    </aside>

    <!-- Área principal -->
    <div class="flex-1 flex flex-col min-h-0 overflow-hidden">
      <!-- Header / toolbar -->
      <header
        class="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-white flex-shrink-0"
      >
        <button
          class="px-3 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 text-slate-700 flex-shrink-0"
          @click="goToToday"
        >
          {{ t('GOOGLE_CALENDAR.TODAY') }}
        </button>
        <div class="flex items-center gap-1 flex-shrink-0">
          <button
            class="p-1 rounded hover:bg-slate-100 text-slate-500"
            @click="navigate(-1)"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
          <button
            class="p-1 rounded hover:bg-slate-100 text-slate-500"
            @click="navigate(1)"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
        </div>
        <h2 class="text-sm font-semibold text-slate-800 flex-shrink-0">
          {{ dateHeader }}
        </h2>

        <!-- Busca de eventos (movido para o header) -->
        <div class="flex-1 mx-2">
          <input
            v-model="eventSearch"
            type="text"
            :placeholder="t('GOOGLE_CALENDAR.SEARCH_EVENTS_PLACEHOLDER')"
            class="w-full max-w-xs rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-woot-500"
          />
        </div>

        <!-- View switcher -->
        <div
          class="flex rounded border border-slate-200 overflow-hidden text-xs flex-shrink-0"
        >
          <button
            v-for="v in views"
            :key="v.key"
            class="px-3 py-1"
            :class="
              view === v.key
                ? 'bg-woot-500 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            "
            @click="view = v.key"
          >
            {{ v.label }}
          </button>
        </div>

        <!-- Modo Telão (TV da clínica) -->
        <button
          class="flex items-center gap-1 px-3 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex-shrink-0"
          :title="t('GOOGLE_CALENDAR.TELAO.BUTTON_HINT')"
          @click="abrirTelao"
        >
          <span class="i-lucide-tv size-3.5" />
          {{ t('GOOGLE_CALENDAR.TELAO.BUTTON') }}
        </button>

        <!-- Botão criar evento -->
        <button
          class="flex items-center gap-1 px-3 py-1 text-xs rounded bg-woot-500 text-white hover:bg-woot-600 flex-shrink-0"
          :disabled="editableCalendars.length === 0"
          :title="
            editableCalendars.length === 0
              ? t('GOOGLE_CALENDAR.NO_EDITABLE_CALENDAR')
              : t('GOOGLE_CALENDAR.NEW_EVENT')
          "
          @click="openCreateModalNow"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('GOOGLE_CALENDAR.NEW_EVENT') }}
        </button>

        <!-- Loading indicator -->
        <span
          v-if="loading"
          class="i-lucide-loader-circle size-4 text-slate-400 animate-spin flex-shrink-0"
        />
      </header>

      <!-- Error banner -->
      <div
        v-if="error"
        class="flex items-center gap-2 px-4 py-2 bg-red-50 border-b border-red-100 text-xs text-red-600 flex-shrink-0"
      >
        <span class="i-lucide-alert-circle size-3.5" />
        {{ error }}
      </div>

      <!-- Grid do calendário -->
      <div class="flex-1 flex flex-col min-h-0 overflow-hidden">
        <!-- View: Mês -->
        <template v-if="view === 'month'">
          <div
            class="grid grid-cols-7 border-b border-slate-100 bg-slate-50 flex-shrink-0"
          >
            <div
              v-for="dayName in [
                'Dom',
                'Seg',
                'Ter',
                'Qua',
                'Qui',
                'Sex',
                'Sáb',
              ]"
              :key="dayName"
              class="py-2 text-center text-xs font-medium text-slate-500"
            >
              {{ dayName }}
            </div>
          </div>
          <div class="flex-1 overflow-auto">
            <div class="grid grid-cols-7">
              <div
                v-for="(day, idx) in monthDays"
                :key="idx"
                class="min-h-24 border-b border-r border-slate-100 p-1 cursor-pointer hover:bg-woot-25"
                :class="!isSameMonth(day) ? 'bg-slate-50' : ''"
                :title="t('GOOGLE_CALENDAR.CREATE_HINT')"
                @click.self="onMonthDayClick(day)"
              >
                <div
                  class="size-6 flex items-center justify-center rounded-full text-xs mb-1"
                  :class="
                    isToday(day)
                      ? 'bg-woot-500 text-white font-semibold'
                      : 'text-slate-700'
                  "
                  @click.stop="onMonthDayClick(day)"
                >
                  {{ day.getDate() }}
                </div>
                <template
                  v-for="event in getEventsForDay(day).slice(0, 3)"
                  :key="event.id"
                >
                  <div
                    class="rounded text-xs px-1 py-0.5 mb-0.5 truncate cursor-pointer text-white"
                    :style="{
                      backgroundColor: eventColor(event),
                    }"
                    :title="eventTooltip(event)"
                    @click.stop="openModal(event)"
                  >
                    {{ event.summary || t('GOOGLE_CALENDAR.NO_TITLE') }}
                  </div>
                </template>
                <div
                  v-if="getEventsForDay(day).length > 3"
                  class="text-xs text-slate-500 cursor-pointer hover:underline"
                  @click.stop="jumpToDay(day)"
                >
                  +{{ getEventsForDay(day).length - 3 }}
                  {{ t('GOOGLE_CALENDAR.MORE_EVENTS') }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- View: Semana -->
        <template v-else-if="view === 'week'">
          <div class="flex border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <div class="w-14 flex-shrink-0" />
            <div
              v-for="(day, i) in weekDays"
              :key="i"
              class="flex-1 text-center py-2"
            >
              <div class="text-xs text-slate-500">{{ shortWeekDay(day) }}</div>
              <div
                class="mx-auto size-7 flex items-center justify-center rounded-full text-sm font-medium"
                :class="
                  isToday(day) ? 'bg-woot-500 text-white' : 'text-slate-700'
                "
              >
                {{ day.getDate() }}
              </div>
            </div>
          </div>
          <div class="flex flex-1 overflow-auto min-h-0">
            <!-- Coluna de horas -->
            <div class="w-14 flex-shrink-0">
              <div
                v-for="hour in hours"
                :key="hour"
                class="h-12 border-b border-slate-50 flex items-start justify-end pr-2 pt-0.5"
              >
                <span v-if="hour > 0" class="text-xs text-slate-400">{{
                  `${hour}:00`
                }}</span>
              </div>
            </div>
            <!-- Colunas dos dias -->
            <div
              v-for="(day, di) in weekDays"
              :key="di"
              class="flex-1 relative border-l border-slate-100"
            >
              <div
                v-for="hour in hours"
                :key="hour"
                class="h-12 border-b border-slate-50 cursor-pointer hover:bg-woot-25"
                :title="t('GOOGLE_CALENDAR.CREATE_HINT')"
                @click="onSlotClick(day, hour, $event)"
              />
              <div
                v-for="event in getEventsForDay(day)"
                :key="event.id"
                class="absolute left-0.5 right-0.5 rounded text-xs px-1 py-0.5 cursor-pointer overflow-hidden text-white"
                :style="{
                  ...eventPosition(event),
                  backgroundColor: eventColor(event),
                }"
                :title="eventTooltip(event)"
                @click.stop="openModal(event)"
              >
                <div class="font-medium truncate">
                  {{ event.summary || t('GOOGLE_CALENDAR.NO_TITLE') }}
                </div>
                <div class="opacity-80">{{ formatTime(event.start) }}</div>
              </div>
            </div>
          </div>
        </template>

        <!-- View: Dia -->
        <template v-else>
          <div class="flex border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <div class="w-14 flex-shrink-0" />
            <div class="flex-1 text-center py-2">
              <div class="text-xs text-slate-500">
                {{ shortWeekDay(currentDate) }}
              </div>
              <div
                class="mx-auto size-8 flex items-center justify-center rounded-full text-sm font-semibold"
                :class="
                  isToday(currentDate)
                    ? 'bg-woot-500 text-white'
                    : 'text-slate-700'
                "
              >
                {{ currentDate.getDate() }}
              </div>
            </div>
          </div>
          <div class="flex flex-1 overflow-auto min-h-0">
            <div class="w-14 flex-shrink-0">
              <div
                v-for="hour in hours"
                :key="hour"
                class="h-12 border-b border-slate-50 flex items-start justify-end pr-2 pt-0.5"
              >
                <span v-if="hour > 0" class="text-xs text-slate-400">{{
                  `${hour}:00`
                }}</span>
              </div>
            </div>
            <div class="flex-1 relative border-l border-slate-100">
              <div
                v-for="hour in hours"
                :key="hour"
                class="h-12 border-b border-slate-50 cursor-pointer hover:bg-woot-25"
                :title="t('GOOGLE_CALENDAR.CREATE_HINT')"
                @click="onSlotClick(currentDate, hour, $event)"
              />
              <div
                v-for="event in getEventsForDay(currentDate)"
                :key="event.id"
                class="absolute left-1 right-1 rounded text-xs px-1 py-0.5 cursor-pointer overflow-hidden text-white"
                :style="{
                  ...eventPosition(event),
                  backgroundColor: eventColor(event),
                }"
                :title="eventTooltip(event)"
                @click.stop="openModal(event)"
              >
                <div class="font-medium truncate">
                  {{ event.summary || t('GOOGLE_CALENDAR.NO_TITLE') }}
                </div>
                <div class="opacity-80">
                  {{ formatTime(event.start) }} – {{ formatTime(event.end) }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal de evento -->
    <Transition name="fade">
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="closeModal"
      >
        <div
          class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden"
        >
          <!-- Header do modal -->
          <div
            class="px-5 py-4 flex items-start gap-3"
            :style="{
              borderLeft: `4px solid ${
                modalMode === 'create'
                  ? '#667eea'
                  : eventColor(selectedEvent || {})
              }`,
            }"
          >
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-semibold text-slate-800 break-words">
                <template v-if="modalMode === 'create'">
                  {{ t('GOOGLE_CALENDAR.MODAL.NEW_EVENT_TITLE') }}
                </template>
                <template v-else>
                  {{ selectedEvent?.summary || t('GOOGLE_CALENDAR.NO_TITLE') }}
                </template>
              </h3>
              <p
                v-if="modalMode !== 'create'"
                class="text-xs text-slate-500 mt-0.5"
              >
                {{ selectedEvent?.calendarName }}
              </p>
            </div>
            <button
              class="text-slate-400 hover:text-slate-600 flex-shrink-0"
              @click="closeModal"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Corpo: visualização -->
          <div v-if="modalMode === 'view'" class="px-5 py-3 space-y-3">
            <div class="flex items-center gap-2 text-sm text-slate-600">
              <span
                class="i-lucide-clock size-4 text-slate-400 flex-shrink-0"
              />
              <span>
                {{ formatDateTime(selectedEvent?.start) }}
                <template v-if="selectedEvent?.end">
                  – {{ formatDateTime(selectedEvent.end) }}</template
                >
              </span>
            </div>
            <div
              v-if="selectedEvent?.location"
              class="flex items-center gap-2 text-sm text-slate-600"
            >
              <span
                class="i-lucide-map-pin size-4 text-slate-400 flex-shrink-0"
              />
              <span>{{ selectedEvent.location }}</span>
            </div>
            <div
              v-if="selectedEvent?.description"
              class="flex items-start gap-2 text-sm text-slate-600"
            >
              <span
                class="i-lucide-align-left size-4 text-slate-400 flex-shrink-0 mt-0.5"
              />
              <span class="whitespace-pre-wrap break-words">{{
                selectedEvent.description
              }}</span>
            </div>
            <div
              v-if="selectedEvent?.attendees?.length"
              class="flex items-start gap-2 text-sm text-slate-600"
            >
              <span
                class="i-lucide-users size-4 text-slate-400 flex-shrink-0 mt-0.5"
              />
              <span>{{ selectedEvent.attendees.join(', ') }}</span>
            </div>
          </div>

          <!-- Corpo: edição / criação -->
          <div v-else class="px-5 py-3 space-y-3">
            <div>
              <label class="text-xs font-medium text-slate-600">{{
                t('GOOGLE_CALENDAR.MODAL.TITLE_LABEL')
              }}</label>
              <input
                v-model="editForm.summary"
                type="text"
                :placeholder="t('GOOGLE_CALENDAR.MODAL.TITLE_PLACEHOLDER')"
                class="mt-1 w-full rounded border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
            <!-- Seletor de calendário (apenas na criação) -->
            <div v-if="modalMode === 'create'">
              <label class="text-xs font-medium text-slate-600">{{
                t('GOOGLE_CALENDAR.MODAL.CALENDAR_LABEL')
              }}</label>
              <select
                v-model="editForm.calendarId"
                class="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option
                  v-for="cal in editableCalendars"
                  :key="cal.id"
                  :value="cal.id"
                >
                  {{ cal.summary }}
                </option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-xs font-medium text-slate-600">{{
                  t('GOOGLE_CALENDAR.MODAL.START_LABEL')
                }}</label>
                <input
                  v-model="editForm.start"
                  type="datetime-local"
                  class="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                />
              </div>
              <div>
                <label class="text-xs font-medium text-slate-600">{{
                  t('GOOGLE_CALENDAR.MODAL.END_LABEL')
                }}</label>
                <input
                  v-model="editForm.end"
                  type="datetime-local"
                  class="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                />
              </div>
            </div>
            <div>
              <label class="text-xs font-medium text-slate-600">{{
                t('GOOGLE_CALENDAR.MODAL.LOCATION_LABEL')
              }}</label>
              <input
                v-model="editForm.location"
                type="text"
                class="mt-1 w-full rounded border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-slate-600">{{
                t('GOOGLE_CALENDAR.MODAL.DESCRIPTION_LABEL')
              }}</label>
              <textarea
                v-model="editForm.description"
                rows="3"
                class="mt-1 w-full rounded border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 resize-none"
              />
            </div>
            <!-- Seletor de cor do evento -->
            <div>
              <label class="text-xs font-medium text-slate-600">{{
                t('GOOGLE_CALENDAR.MODAL.COLOR_LABEL')
              }}</label>
              <div class="mt-1.5 flex items-center gap-1.5 flex-wrap">
                <!-- Cor padrão do calendário -->
                <button
                  type="button"
                  class="size-6 rounded-full border flex items-center justify-center transition"
                  :class="
                    !editForm.colorId
                      ? 'ring-2 ring-offset-1 ring-woot-500 border-transparent'
                      : 'border-slate-300'
                  "
                  :title="t('GOOGLE_CALENDAR.MODAL.COLOR_DEFAULT')"
                  @click="editForm.colorId = ''"
                >
                  <span class="i-lucide-ban size-3.5 text-slate-400" />
                </button>
                <button
                  v-for="color in GOOGLE_COLORS"
                  :key="color.id"
                  type="button"
                  class="size-6 rounded-full transition flex items-center justify-center"
                  :class="
                    editForm.colorId === color.id
                      ? 'ring-2 ring-offset-1 ring-woot-500'
                      : ''
                  "
                  :style="{ backgroundColor: color.hex }"
                  :title="t(`GOOGLE_CALENDAR.COLORS.${color.key}`)"
                  @click="editForm.colorId = color.id"
                >
                  <span
                    v-if="editForm.colorId === color.id"
                    class="i-lucide-check size-3.5 text-white"
                  />
                </button>
              </div>
            </div>
          </div>

          <!-- Footer do modal -->
          <div
            class="px-5 py-3 border-t border-slate-100 flex items-center gap-2"
          >
            <!-- Modo: visualização -->
            <template v-if="modalMode === 'view'">
              <button
                v-if="selectedEvent?.isEditable"
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-woot-500 text-white hover:bg-woot-600"
                @click="enterEdit"
              >
                <span class="i-lucide-pencil size-3" />
                {{ t('GOOGLE_CALENDAR.MODAL.EDIT') }}
              </button>
              <button
                v-if="selectedEvent?.isEditable"
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500 text-white hover:bg-red-600"
                :disabled="loading"
                @click="confirmDelete"
              >
                <span class="i-lucide-trash-2 size-3" />
                {{ t('GOOGLE_CALENDAR.MODAL.DELETE') }}
              </button>
              <span
                v-if="!selectedEvent?.isEditable"
                class="text-xs text-slate-400 italic"
                >{{ t('GOOGLE_CALENDAR.MODAL.READ_ONLY') }}</span
              >
              <button
                class="ml-auto px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                @click="closeModal"
              >
                {{ t('GOOGLE_CALENDAR.MODAL.CLOSE') }}
              </button>
            </template>
            <!-- Modo: edição -->
            <template v-else-if="modalMode === 'edit'">
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-woot-500 text-white hover:bg-woot-600"
                :disabled="loading"
                @click="saveEvent"
              >
                <span class="i-lucide-check size-3" />
                {{ t('GOOGLE_CALENDAR.MODAL.SAVE') }}
              </button>
              <button
                class="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                @click="cancelEdit"
              >
                {{ t('GOOGLE_CALENDAR.MODAL.CANCEL') }}
              </button>
            </template>
            <!-- Modo: criação -->
            <template v-else>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-woot-500 text-white hover:bg-woot-600"
                :disabled="loading"
                @click="saveNewEvent"
              >
                <span class="i-lucide-plus size-3" />
                {{ t('GOOGLE_CALENDAR.MODAL.CREATE') }}
              </button>
              <button
                class="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                @click="cancelEdit"
              >
                {{ t('GOOGLE_CALENDAR.MODAL.CANCEL') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
